import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { DateTime } from 'luxon'
import Avatar from '@material-ui/core/Avatar'
import Box from '@material-ui/core/Box'
import Button from '@material-ui/core/Button'
import IconButton from '@material-ui/core/IconButton'
import Tooltip from '@material-ui/core/Tooltip'
import Typography from '@material-ui/core/Typography'
import Rating from 'material-ui-rating'
import LinesEllipsis from 'react-lines-ellipsis'
import CopyTextButton from 'components/copy-text-button'
import FileCopyOutlinedIcon from 'components/material-symbol/icons/FileCopy'
import ReviewModal from 'components/review-modal'
import useStyles from './coach-review-card.styles'
import { coachReviewCardRatingIcons } from './rating-icons'

const getReviewerInitials = (firstName, lastName) =>
  `${firstName?.substring(0, 1) || ''}${lastName?.substring(0, 1) ||
    ''}`.toUpperCase()

const formatReviewDate = updatedAt =>
  DateTime.fromSQL(updatedAt).toFormat('MMM d, yyyy')

const getReviewCopyText = ({ reviewText, contact, updatedAt }) => {
  const { firstName, lastName } = contact
  const name = `${firstName} ${lastName}`.trim()
  const date = formatReviewDate(updatedAt)

  return [name, date, reviewText].filter(Boolean).join('\n')
}

const CoachReviewCard = props => {
  const { review, isMobile } = props
  const { rating, reviewText, updatedAt, contact } = review
  const { firstName, lastName } = contact

  const [maxLines, setMaxLines] = useState(4)
  const [isReviewClamped, setIsReviewClamped] = useState(false)
  const [showFullReview, setShowFullReview] = useState(false)
  const [openDialog, setOpenDialog] = useState(false)
  const MAX_LINES = 50
  const styles = useStyles()

  const handleReflow = rleState => {
    setIsReviewClamped(rleState.clamped)
  }

  const handleFullReview = e => {
    e.preventDefault()
    if (isMobile) {
      setMaxLines(MAX_LINES)
      setShowFullReview(true)
    } else {
      setOpenDialog(true)
    }
  }

  const toggleDialog = isOpenDialog => {
    setOpenDialog(isOpenDialog)
  }

  const showReadMore = isReviewClamped && !showFullReview
  const showCopyTestimonial = Boolean(reviewText)

  return (
    <Box className={styles.reviewCard}>
      <Box className={styles.reviewHeader}>
        <Avatar className={styles.reviewerAvatar}>
          {getReviewerInitials(firstName, lastName)}
        </Avatar>
        <Box className={styles.reviewerMeta}>
          <Typography className={styles.reviewerName}>
            {firstName} {lastName}
          </Typography>
          <Typography className={styles.reviewDate}>
            {formatReviewDate(updatedAt)}
          </Typography>
        </Box>
        <Box className={styles.reviewRating}>
          <Rating
            name={`review-rating-${review.id}`}
            value={rating}
            readOnly
            disableHover
            classes={{
              iconButton: styles.starButton,
            }}
            {...coachReviewCardRatingIcons}
          />
        </Box>
      </Box>

      <Box className={styles.reviewTextWrapper}>
        {showFullReview ? (
          <Typography className={styles.reviewText}>
            {reviewText || ''}
          </Typography>
        ) : (
          <LinesEllipsis
            maxLine={maxLines}
            text={reviewText || ''}
            className={styles.reviewText}
            onReflow={handleReflow}
          />
        )}
      </Box>

      {showReadMore || showCopyTestimonial ? (
        <Box className={styles.reviewFooter}>
          <Box className={styles.reviewActions}>
            {showReadMore ? (
              <>
                <Button
                  disableRipple
                  className={styles.reviewAction}
                  onClick={handleFullReview}
                >
                  Read more
                </Button>
                <ReviewModal
                  review={review}
                  openDialog={openDialog}
                  parentToggleDialog={toggleDialog}
                />
              </>
            ) : null}
          </Box>
          {showCopyTestimonial ? (
            <Tooltip title="Copy testimonial">
              <CopyTextButton
                component={IconButton}
                value={getReviewCopyText({ reviewText, contact, updatedAt })}
                notificationText="Testimonial copied to clipboard"
                className={styles.copyButton}
                aria-label="Copy testimonial"
                size="small"
              >
                <FileCopyOutlinedIcon className={styles.copyIcon} aria-hidden />
              </CopyTextButton>
            </Tooltip>
          ) : null}
        </Box>
      ) : null}
    </Box>
  )
}

CoachReviewCard.propTypes = {
  review: PropTypes.object,
  isMobile: PropTypes.bool,
}

export default CoachReviewCard
